import React from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

/**
 * Props for the TripNotFoundPanel component.
 */
interface TripNotFoundPanelProps {
	/** Callback fired when the back button is pressed. */
	onBack: () => void;
	/** Label for the back button. Default: "Back to trips" */
	backLabel?: string;
}

/**
 * Branded empty state shown when a requested trip does not exist.
 *
 * Mirrors the layout of `TelematicsErrorPanel` (icon in a tinted circle,
 * heading, message, action) but uses the secondary brand colour rather than
 * the error colour, because a missing trip is not a failure.
 *
 * @component
 * @example
 * ```tsx
 * <TripNotFoundPanel onBack={() => navigate(-1)} />
 * ```
 */
const TripNotFoundPanel: React.FC<TripNotFoundPanelProps> = ({ onBack, backLabel = 'Back to trips' }) => {
	const theme = useTheme();

	return (
		<Box
			role='status'
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				py: 8,
				px: 3,
				textAlign: 'center',
			}}
		>
			<Box
				sx={{
					width: 80,
					height: 80,
					borderRadius: '50%',
					backgroundColor: alpha(theme.palette.secondary.main, 0.08),
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					mb: 3,
				}}
			>
				<SearchOffIcon sx={{ fontSize: 40, color: theme.palette.secondary.main }} />
			</Box>

			<Typography variant='h6' component='h2' sx={{ fontWeight: 600, color: theme.palette.secondary.main, mb: 1.5 }}>
				Trip not found
			</Typography>

			<Typography variant='body1' sx={{ color: theme.palette.text.secondary, maxWidth: 400, mb: 4, lineHeight: 1.6 }}>
				We couldn&apos;t find this trip. It may no longer be available, or the link you followed may be out of date.
			</Typography>

			<Button
				variant='outlined'
				color='primary'
				startIcon={<ArrowBackIcon />}
				onClick={onBack}
				sx={{ textTransform: 'none', px: 3, py: 1 }}
			>
				{backLabel}
			</Button>
		</Box>
	);
};

export default TripNotFoundPanel;
